import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConsent } from '../context/ConsentContext';
import { COLORS } from '../constants/colors';

const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
`;

const Bar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2000;
  background: #fff;
  border-top: 1px solid #dee2e6;
  box-shadow: 0 -6px 24px rgba(0, 0, 0, 0.12);
  padding: 20px 24px;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${slideUp} 0.3s ease-out;
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const Copy = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h2`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: ${COLORS.PRIMARY};
  margin: 0 0 6px 0;
`;

const Text = styled.p`
  font-size: 0.9rem;
  line-height: 1.55;
  color: #495057;
  margin: 0;

  a {
    color: ${COLORS.PRIMARY};
    font-weight: 600;
  }
`;

/*
 * Accept and Reject are intentionally identical in size, weight and
 * prominence. Making refusal harder than acceptance invalidates the
 * consent under GDPR, so this symmetry is a requirement, not a style.
 */
const Actions = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 900px) {
    width: 100%;
  }
`;

const ChoiceButton = styled.button<{ $primary?: boolean }>`
  flex: 1;
  min-width: 150px;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1.5px solid ${COLORS.PRIMARY};
  background: ${p => (p.$primary ? COLORS.PRIMARY : '#fff')};
  color: ${p => (p.$primary ? '#fff' : COLORS.PRIMARY)};

  &:hover {
    background: ${p => (p.$primary ? COLORS.HOVER_LIGHT : `${COLORS.PRIMARY}12`)};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.PRIMARY};
    outline-offset: 2px;
  }

  @media (max-width: 900px) {
    min-width: 0;
  }
`;

export const CookieBanner: React.FC = () => {
  const { isBannerOpen, acceptAll, rejectAll } = useConsent();
  const { t } = useTranslation();

  if (!isBannerOpen) return null;

  return (
    <Bar role="dialog" aria-live="polite" aria-label={t('cookies.banner.title', 'Cookies')}>
      <Inner>
        <Copy>
          <Title>{t('cookies.banner.title', 'Cookies')}</Title>
          <Text>
            {t(
              'cookies.banner.description',
              'We use cookies that are needed to run the site — signing in, your cart, your language. With your permission we would also use marketing cookies to measure our ads. You can change your mind at any time.'
            )}{' '}
            <Link to="/privacy">{t('cookies.banner.privacyLink', 'Read our privacy policy')}</Link>
          </Text>
        </Copy>
        <Actions>
          <ChoiceButton onClick={rejectAll}>
            {t('cookies.banner.reject', 'Only necessary')}
          </ChoiceButton>
          <ChoiceButton $primary onClick={acceptAll}>
            {t('cookies.banner.accept', 'Accept all')}
          </ChoiceButton>
        </Actions>
      </Inner>
    </Bar>
  );
};
